import React, { useState, Fragment } from 'react';
import { Search, Menu, X, ChevronRight, ChevronDown, BookOpen, Code } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
interface DocsLayoutProps {
  children: React.ReactNode;
  breadcrumbs?: {
    label: string;
    href: string;
  }[];
  onThisPage?: {
    title: string;
    id: string;
  }[];
}
const sidebarNav = [{
  title: 'Getting Started',
  items: [{
    label: 'Documentation Home',
    href: '/docs'
  }, {
    label: 'Quick Start',
    href: '/docs/guides/getting-started'
  }]
}, {
  title: 'Guides',
  items: [{
    label: 'Authentication',
    href: '/docs/guides/authentication'
  }, {
    label: 'Idempotency',
    href: '/docs/guides/idempotency'
  }, {
    label: 'Webhooks',
    href: '/docs/guides/webhooks'
  }, {
    label: 'Errors',
    href: '/docs/guides/errors'
  }, {
    label: 'Pagination',
    href: '/docs/guides/pagination'
  }]
}, {
  title: 'API Reference',
  items: [{
    label: 'Overview',
    href: '/docs/api-reference'
  }, {
    label: 'API Conventions',
    href: '/docs/api-conventions'
  }, {
    label: 'Orchestrate',
    href: '/docs/api-reference/orchestrate'
  }, {
    label: 'Payments',
    href: '/docs/api-reference/payments'
  }, {
    label: 'Wallets',
    href: '/docs/api-reference/wallets'
  }, {
    label: 'Exchange',
    href: '/docs/api-reference/exchange'
  }, {
    label: 'Compliance',
    href: '/docs/api-reference/compliance'
  }, {
    label: 'Liquidity',
    href: '/docs/api-reference/liquidity'
  }, {
    label: 'Tokenize',
    href: '/docs/api-reference/tokenize'
  }, {
    label: 'Connect',
    href: '/docs/api-reference/connect'
  }]
}, {
  title: 'Support',
  items: [{
    label: 'Contact Support',
    href: '/docs/support'
  }, {
    label: 'API Status',
    href: '#'
  }, {
    label: 'Changelog',
    href: '#'
  }]
}];
export function DocsLayout({
  children,
  breadcrumbs,
  onThisPage
}: DocsLayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState<string[]>(['Getting Started', 'Guides', 'API Reference']);
  const toggleSection = (title: string) => {
    setExpandedSections(prev => prev.includes(title) ? prev.filter(t => t !== title) : [...prev, title]);
  };
  return <div className="min-h-screen bg-white pt-20" data-id="element-32">
      {/* Top Bar */}
      <div className="fixed top-16 left-0 right-0 z-40 bg-white border-b border-slate-200" data-id="element-33">
        <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 py-3" data-id="element-34">
          <div className="flex items-center justify-between gap-4" data-id="element-35">
            {/* Mobile Menu Button */}
            <button onClick={() => setSidebarOpen(!sidebarOpen)} className="lg:hidden p-2 text-slate-600 hover:text-slate-900" data-id="element-36">
              {sidebarOpen ? <X size={20} data-id="element-37" /> : <Menu size={20} data-id="element-38" />}
            </button>

            {/* Search Bar */}
            <div className="flex-1 max-w-2xl" data-id="element-39">
              <div className="relative" data-id="element-40">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-41" />
                <input type="text" placeholder="Search documentation..." className="w-full pl-10 pr-4 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-42" />
              </div>
            </div>

            {/* Version Badge */}
            <div className="hidden sm:flex items-center gap-2 px-3 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded-full border border-blue-100" data-id="element-43">
              API v1
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 pt-20" data-id="element-44">
        <div className="flex gap-8" data-id="element-45">
          {/* Left Sidebar */}
          <AnimatePresence data-id="element-46">
            {(sidebarOpen || window.innerWidth >= 1024) && <motion.aside initial={{
            x: -300,
            opacity: 0
          }} animate={{
            x: 0,
            opacity: 1
          }} exit={{
            x: -300,
            opacity: 0
          }} className="fixed lg:sticky top-36 left-0 w-64 h-[calc(100vh-9rem)] overflow-y-auto bg-white border-r border-slate-200 lg:border-0 z-30 lg:z-0" data-id="element-47">
                <nav className="p-4 space-y-6" data-id="element-48">
                  {sidebarNav.map(section => <div key={section.title} data-id="element-49">
                      <button onClick={() => toggleSection(section.title)} className="flex items-center justify-between w-full text-xs font-semibold text-slate-900 uppercase tracking-wider mb-2 hover:text-blue-600 transition-colors" data-id="element-50">
                        {section.title}
                        {expandedSections.includes(section.title) ? <ChevronDown className="w-3 h-3" data-id="element-51" /> : <ChevronRight className="w-3 h-3" data-id="element-52" />}
                      </button>
                      <AnimatePresence data-id="element-53">
                        {expandedSections.includes(section.title) && <motion.div initial={{
                    height: 0,
                    opacity: 0
                  }} animate={{
                    height: 'auto',
                    opacity: 1
                  }} exit={{
                    height: 0,
                    opacity: 0
                  }} className="space-y-1 overflow-hidden" data-id="element-54">
                            {section.items.map(item => <a key={item.href} href={`#${item.href}`} className="block px-3 py-1.5 text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-50 rounded transition-colors" data-id="element-55">
                                {item.label}
                              </a>)}
                          </motion.div>}
                      </AnimatePresence>
                    </div>)}
                </nav>
              </motion.aside>}
          </AnimatePresence>

          {/* Main Content */}
          <main className="flex-1 min-w-0 max-w-4xl" data-id="element-56">
            {/* Breadcrumbs */}
            {breadcrumbs && breadcrumbs.length > 0 && <div className="flex items-center gap-2 text-sm text-slate-600 mb-6" data-id="element-57">
                {breadcrumbs.map((crumb, index) => <Fragment key={crumb.href} data-id="element-58">
                    {index > 0 && <ChevronRight className="w-4 h-4" data-id="element-59" />}
                    <a href={`#${crumb.href}`} className={`hover:text-slate-900 transition-colors ${index === breadcrumbs.length - 1 ? 'text-slate-900 font-medium' : ''}`} data-id="element-60">
                      {crumb.label}
                    </a>
                  </Fragment>)}
              </div>}

            {/* Content */}
            <div className="prose prose-slate max-w-none" data-id="element-61">{children}</div>
          </main>

          {/* Right Sidebar - On This Page */}
          {onThisPage && onThisPage.length > 0 && <aside className="hidden xl:block w-56 sticky top-36 h-[calc(100vh-9rem)] overflow-y-auto" data-id="element-62">
              <div className="p-4" data-id="element-63">
                <h3 className="text-xs font-semibold text-slate-900 uppercase tracking-wider mb-3" data-id="element-64">
                  On This Page
                </h3>
                <nav className="space-y-2" data-id="element-65">
                  {onThisPage.map(item => <a key={item.id} href={`#${item.id}`} className="block text-sm text-slate-600 hover:text-slate-900 transition-colors" data-id="element-66">
                      {item.title}
                    </a>)}
                </nav>
              </div>
            </aside>}
        </div>
      </div>
    </div>;
}